import { createSlice } from "@reduxjs/toolkit";
import {
  createErrorReportingAsyncThunk,
  serverRejectionMessage,
} from "../../helpers";
import type { Dictionary } from "lodash";
import axios, { getData } from "../../../axios";
import {
  BaseEquipmentType,
  EquipmentType,
  decodeEquipmentType,
  decodeEquipmentTypes,
  UpdateEquipmentType,
} from "./types";
import keyBy from "lodash/keyBy";
import { notifyL } from "../../notifications";
import {
  DUPLICATE_EQUIPMENT_TYPE_NAME,
  EQUIPMENT_TYPE_HAS_EQUIPMENT,
} from "dora-shared";

const prefix = "data/equipment-types";

type State = {
  entities: Dictionary<EquipmentType>;
  ids: string[];
  // Keyed by equipment type id. Set when an archive attempt is rejected
  // because equipment still references the type - shown as the archive
  // button's tooltip rather than a generic toast, since it's specific to
  // that row.
  archiveErrors: Dictionary<string>;
  loading: boolean;
};

const initialState: State = {
  entities: {},
  ids: [],
  archiveErrors: {},
  loading: false,
};

export const loadEquipmentTypes = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async (): Promise<EquipmentType[]> => {
    return await axios
      .get("/api/equipment/types")
      .then(getData)
      .then(decodeEquipmentTypes);
  }
);

// The type name is unique per org, so the server answers 409
// DUPLICATE_EQUIPMENT_TYPE_NAME when it is taken. That refusal is aimed at the
// user: its own translated toast, and the rejection value, which leaves the
// dialog open. Everything else is rethrown untouched, so
// createErrorReportingAsyncThunk reports it exactly once.
export const addEquipmentType = createErrorReportingAsyncThunk<
  EquipmentType,
  BaseEquipmentType,
  { rejectValue: string }
>(`${prefix}/add`, async (values, thunkApi) => {
  try {
    const equipmentType = await axios
      .post("/api/equipment/types", values)
      .then(getData)
      .then(decodeEquipmentType);
    thunkApi.dispatch(
      notifyL({
        namespace: "notifications",
        key: "equipmentTypeSaved",
        type: "success",
      })
    );
    return equipmentType;
  } catch (err) {
    const rejection = serverRejectionMessage(err);
    if (
      rejection?.kind === "code" &&
      rejection.code === DUPLICATE_EQUIPMENT_TYPE_NAME
    ) {
      thunkApi.dispatch(
        notifyL({
          namespace: "notifications",
          key: "errorSavingEquipmentTypeDuplicateName",
          type: "error",
        })
      );
      return thunkApi.rejectWithValue(DUPLICATE_EQUIPMENT_TYPE_NAME);
    }
    throw err;
  }
});

export const updateEquipmentType = createErrorReportingAsyncThunk(
  `${prefix}/update`,
  async (
    equipmentType: UpdateEquipmentType,
    thunkApi
  ): Promise<EquipmentType> => {
    const updated = await axios
      .put(`/api/equipment/types/${equipmentType.id}`, equipmentType)
      .then(getData)
      .then(decodeEquipmentType);
    thunkApi.dispatch(
      notifyL({
        namespace: "notifications",
        key: "equipmentTypeSaved",
        type: "success",
      })
    );
    return updated;
  }
);

export const archiveEquipmentType = createErrorReportingAsyncThunk<
  void,
  string,
  { rejectValue: string }
>(`${prefix}/archive`, async (id, thunkApi) => {
  try {
    await axios.post(`/api/equipment/types/${id}/archive`);
  } catch (err) {
    const rejection = serverRejectionMessage(err);
    if (
      rejection?.kind === "code" &&
      rejection.code === EQUIPMENT_TYPE_HAS_EQUIPMENT
    ) {
      thunkApi.dispatch(
        notifyL({
          namespace: "notifications",
          key: "errorArchivingEquipmentTypeHasEquipment",
          type: "error",
        })
      );
      return thunkApi.rejectWithValue(EQUIPMENT_TYPE_HAS_EQUIPMENT);
    }
    throw err;
  }
});

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadEquipmentTypes.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadEquipmentTypes.rejected, (state) => {
        state.loading = false;
      })
      .addCase(loadEquipmentTypes.fulfilled, (state, action) => {
        state.loading = false;
        const types = action.payload;
        state.entities = keyBy(types, "id");
        state.ids = types.map((x) => x.id);
      })
      .addCase(addEquipmentType.fulfilled, (state, action) => {
        const type = action.payload;
        state.ids.unshift(type.id);
        state.entities[type.id] = type;
      })
      .addCase(updateEquipmentType.fulfilled, (state, action) => {
        const type = action.payload;
        state.entities[type.id] = type;
      })
      .addCase(archiveEquipmentType.pending, (state, action) => {
        delete state.archiveErrors[action.meta.arg];
      })
      .addCase(archiveEquipmentType.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.entities[id];
        state.ids = state.ids.filter((x) => x !== id);
        delete state.archiveErrors[id];
      })
      .addCase(archiveEquipmentType.rejected, (state, action) => {
        if (action.payload === EQUIPMENT_TYPE_HAS_EQUIPMENT) {
          state.archiveErrors[action.meta.arg] = EQUIPMENT_TYPE_HAS_EQUIPMENT;
        }
      });
  },
});

export default slice.reducer;
